import type { ReactNode } from 'react';
import type { ForgeUsageSheet, UsageSheetLinkField } from '@shared/types/forge-usage-sheet';
import { SheetRow, SheetSection } from './SheetLines';
import { removeAt, replaceAt } from './sheet-edits';

type Text = (value: string, label: string, onSave: (value: string) => void) => ReactNode;

const fieldsLine = (fields: UsageSheetLinkField[]): string =>
  fields.map((field) => `${field.inputLabel} ← ${field.columnLabel}`).join(' · ');

const statusesLine = (labels: string[]): string => (labels.length > 0 ? ` · si ${labels.join(' ou ')}` : '');

/**
 * S1 — the tool among the others: the list it starts from (« Point de départ »),
 * the tools proposed on its objects (« Ensuite ») and the documents it reads,
 * each with the role the person validates. Only sections with a line show.
 */
export const LinksSections = ({ sheet, editable, showWhy, text, set }: {
  sheet: ForgeUsageSheet;
  editable: boolean;
  showWhy: boolean;
  text: Text;
  set: (patch: Partial<ForgeUsageSheet>) => void;
}) => {
  const startingPoints = sheet.startingPoints ?? [];
  const nextTools = sheet.nextTools ?? [];
  const documents = sheet.documents ?? [];
  const appStatusLabel = (key: string) => sheet.appData?.statuses.find((status) => status.key === key)?.label ?? key;
  return <>
    {startingPoints.length > 0 && <SheetSection title="Point de départ">
      {startingPoints.map((point, index) => <SheetRow key={point.appId} showWhy={showWhy}
        why={<>{point.why}{point.fields.length > 0 && <span className="block">{fieldsLine(point.fields)}</span>}</>}
        removeLabel={`Retirer le point de départ ${point.appTitle}`}
        onRemove={editable ? () => set({ startingPoints: removeAt(startingPoints, index) }) : undefined}>
        Une ligne de « {point.appTitle} »{statusesLine(point.statusLabels ?? [])}
      </SheetRow>)}
    </SheetSection>}

    {nextTools.length > 0 && <SheetSection title="Ensuite">
      {nextTools.map((tool, index) => <SheetRow key={tool.flowId} showWhy={showWhy}
        why={<>{tool.why}{tool.fields.length > 0 && <span className="block">{fieldsLine(tool.fields)}</span>}</>}
        removeLabel={`Retirer ${tool.title}`}
        onRemove={editable ? () => set({ nextTools: removeAt(nextTools, index) }) : undefined}>
        {tool.title}{statusesLine(tool.statuses.map(appStatusLabel))}
      </SheetRow>)}
    </SheetSection>}

    {documents.length > 0 && <SheetSection title="Documents">
      {documents.map((document, index) => <SheetRow key={document.documentId} showWhy={false}
        removeLabel={`Retirer le document ${document.title}`}
        onRemove={editable ? () => set({ documents: removeAt(documents, index) }) : undefined}>
        <span className="font-medium">{document.title}</span>
        {' — '}{text(document.role, `Rôle de ${document.title}`, (role) => set({ documents: replaceAt(documents, index, { role }) }))}
      </SheetRow>)}
    </SheetSection>}
  </>;
};
